/**
 * Application Sidebar Component for Cedar Heights Music Academy Admin System
 * 
 * This component provides role-based navigation with:
 * - Collapsible sidebar with icons and labels
 * - Role-specific menu items
 * - Active state indicators
 * - Responsive behavior (overlay on mobile, persistent on desktop)
 * - Smooth transitions and animations
 */

import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  EventAvailable as ScheduleIcon,
  Payment as StripeIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  ReceiptLong as EnrollmentIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserRole } from '../../types';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavigationItem[];
  roles: UserRole[];
}

interface AppSidebarProps {
  userRole: UserRole;
  open: boolean;
  onClose: () => void;
  width: number;
  collapsedWidth: number;
  variant: 'temporary' | 'persistent';
}

// Navigation configuration based on user roles
const navigationItems: NavigationItem[] = [
  {
    id: 'schedule',
    label: 'Schedule',
    icon: <ScheduleIcon />,
    path: '/schedule',
    roles: ['admin'],
  },
  {
    id: 'stripe',
    label: 'Stripe',
    icon: <StripeIcon />,
    path: '/stripe',
    roles: ['admin'],
  },
  {
    id: 'enrollments',
    label: 'Students',
    icon: <EnrollmentIcon />,
    path: '/students',
    roles: ['admin'],
  },
  {
    id: 'settings',
    label: 'Configuration',
    icon: <SettingsIcon />,
    roles: ['admin'],
    path: '/site',
  },
];

/**
 * Sidebar navigation component with role-based menu items
 */
export const AppSidebar: React.FC<AppSidebarProps> = ({
  userRole,
  open,
  onClose,
  width,
  collapsedWidth,
  variant,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  // Filter navigation items based on user role
  const getFilteredItems = (items: NavigationItem[]): NavigationItem[] => {
    return items
      .filter(item => item.roles.includes(userRole))
      .map(item => ({
        ...item,
        children: item.children ? getFilteredItems(item.children) : undefined,
      }));
  };

  const filteredItems = getFilteredItems(navigationItems);

  const handleItemClick = (item: NavigationItem) => {
    if (item.path) {
      navigate(item.path);
      if (variant === 'temporary') {
        onClose();
      }
    } else if (item.children) {
      handleExpandToggle(item.id);
    }
  };

  const handleExpandToggle = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isItemActive = (item: NavigationItem): boolean => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    return false;
  };

  const renderNavigationItem = (item: NavigationItem, level: number = 0) => {
    const isActive = isItemActive(item);
    const isExpanded = expandedItems.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const isSubmenu = level > 0;

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            selected={isActive}
            sx={{
              paddingLeft: theme.spacing(2 + level * 2),
              paddingY: theme.spacing(1),
              borderRadius: theme.spacing(1),
              marginX: theme.spacing(1),
              '&.Mui-selected': isSubmenu ? {
                // Submenu item styling - more subtle
                backgroundColor: theme.palette.action.selected,
                color: theme.palette.text.primary,
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '& .MuiListItemIcon-root': {
                  color: theme.palette.primary.main,
                },
              } : {
                // Main menu item styling - bold
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '& .MuiListItemIcon-root': {
                  color: theme.palette.primary.contrastText,
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: isActive ? 'inherit' : theme.palette.text.secondary,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: level > 0 ? '0.875rem' : '1rem',
                fontWeight: isActive ? 600 : 400,
              }}
            />
            {hasChildren && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map(child => renderNavigationItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header Spacer */}
      <Box
        sx={{
          height: {
            xs: 56,
            sm: 64,
          },
        }}
      />

      {/* Role Indicator */}
      <Box sx={{ padding: theme.spacing(2), paddingBottom: theme.spacing(1) }}>
        <Typography
          variant="overline"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: 1,
          }}
        >
          {userRole === 'parent' ? 'Parent Portal' :
           userRole === 'teacher' ? 'Teacher Mode' : 'Admin Mode'}
        </Typography>
      </Box>

      <Divider />

      {/* Navigation Items */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', paddingY: theme.spacing(1) }}>
        <List>
          {filteredItems.map(item => renderNavigationItem(item))}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ padding: theme.spacing(2) }}>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            display: 'block',
            textAlign: 'center',
          }}
        >
          Cedar Heights Music Academy
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            display: 'block',
            textAlign: 'center',
          }}
        >
          Admin System v1.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: open ? width : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? width : collapsedWidth,
          boxSizing: 'border-box',
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default AppSidebar;
